import { DialogConfirm } from 'app/components/elements/DialogConfirm';
import { Stack } from '@mui/material';
import { InputText } from 'app/components/elements/InputText';
import { InputSelect } from 'app/components/elements/InputSelect';
import { SelectChangeEvent } from '@mui/material/Select';
import { Controller, useForm } from 'react-hook-form';
import { ButtonCustom, ButtonOutLine } from 'app/components/elements/ButtonCustom';
import { useEffect } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { TriggerTypeFormSchema } from 'utils/validate';
import { TriggerType, TriggerTypeForm } from '../../../type';
import { TriggerSubTypeSection } from './TriggerSubTypeSection';
import { RUNTIME_TRIGGER_TYPE_CODE } from 'store/triggerType';

const CALCULATION_TYPE_OPTIONS = [
  { value: 'COUNTER_BASED', label: 'Counter Based' },
  { value: 'DATE_BASED', label: 'Date Based' }
];

type ModalAddNewTriggerTypeProp = {
  isEdit?: boolean;
  isOpen: boolean;
  initData?: TriggerType;
  onCloseModal: () => void;
  onSubmit: (value: TriggerTypeForm) => void;
};

export const ModalAddNewTriggerType = ({
  isOpen,
  onCloseModal,
  onSubmit,
  isEdit,
  initData
}: ModalAddNewTriggerTypeProp) => {
  const isRuntime = isEdit && initData?.code === RUNTIME_TRIGGER_TYPE_CODE;

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset
  } = useForm<TriggerTypeForm>({
    // yup's inferred `calculationType` (required, no '') is narrower than the
    // '' default this form starts with — same cast used by
    // ModalCreateAssetEntryForm.tsx for the same kind of mismatch.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: yupResolver(TriggerTypeFormSchema) as any,
    defaultValues: { name: '', calculationType: '' }
  });

  useEffect(() => {
    if (!isOpen) {
      reset({ name: '', calculationType: '' });
      return;
    }
    if (isEdit && initData) {
      reset({
        name: initData.name,
        // Runtime's calculationType is fixed and not user-editable — force it
        // regardless of what the API returns, matching the hidden field below.
        calculationType:
          initData.code === RUNTIME_TRIGGER_TYPE_CODE ? 'COUNTER_BASED' : initData.calculationType
      });
      return;
    }
    reset({ name: '', calculationType: '' });
  }, [isOpen, isEdit, initData, reset]);

  return (
    <DialogConfirm
      maxWidth={480}
      textTitle={isEdit ? 'Edit Trigger Type' : 'Add New Trigger Type'}
      isOpen={isOpen}
      onClose={onCloseModal}>
      <Stack width={'100%'} gap={2}>
        <InputText
          colorTitle={'#226AAA'}
          inputError={errors.name?.message}
          placeholder={'Enter name'}
          title={'Name'}
          inputProps={{
            ...register('name')
          }}
        />

        {!isRuntime && (
          <Controller
            control={control}
            name={'calculationType'}
            render={({ field: { onChange, value } }) => (
              <InputSelect
                colorTitle={'#226AAA'}
                title={'Calculation Type'}
                value={value || ''}
                placeholder={'Select Calculation Type'}
                onChange={(e: SelectChangeEvent<unknown>) => onChange(e.target.value as string)}
                sx={{ padding: 0, minHeight: '46px', height: '46px' }}
                options={CALCULATION_TYPE_OPTIONS}
                inputError={errors.calculationType?.message}
              />
            )}
          />
        )}

        {isRuntime && <TriggerSubTypeSection />}

        <Stack
          width={'100%'}
          mt={'10px'}
          alignItems={'center'}
          justifyContent={'center'}
          flexDirection={'row'}
          gap={'20px'}>
          <ButtonOutLine
            label={'Cancel'}
            style={{ width: `calc((100% - 20px)/2)` }}
            onClick={onCloseModal}
            fullWidth
          />
          <ButtonCustom
            label={isEdit ? 'Save' : 'Add'}
            style={{ width: `calc((100% - 20px)/2)` }}
            onClick={handleSubmit(onSubmit)}
            fullWidth
          />
        </Stack>
      </Stack>
    </DialogConfirm>
  );
};
